import { divider, primary, text } from "@/theme/colors";
import { USER_SETTING_COLUMN_GAP, USER_SETTING_CONTENT_WIDTH, USER_SETTING_NAV_WIDTH } from "@/features/user-settings/constants/sections";
import { Box, Stack, Typography, css, styled } from "@mui/material";
import { FC, ReactNode, useEffect, useRef, useState } from "react";

export type TUserSettingSection = {
  id: string;
  title: string;
  content: ReactNode;
};

type TUserSettingLayoutProps = {
  sections: TUserSettingSection[];
  footer?: ReactNode;
};

const SCROLL_OFFSET = 80;

export const UserSettingLayout: FC<TUserSettingLayoutProps> = ({ sections, footer }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: `-${SCROLL_OFFSET}px 0px -55% 0px`, threshold: 0 },
    );
    sections.forEach((section) => {
      const el = refs.current[section.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  const handleClickNav = (id: string) => {
    refs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id);
  };

  return (
    <Row>
      <Nav>
        {sections.map((section) => (
          <NavItem key={section.id} active={activeId === section.id} onClick={() => handleClickNav(section.id)}>
            <Typography variant="body14" color={text.primary}>
              {section.title}
            </Typography>
          </NavItem>
        ))}
      </Nav>

      <Content>
        {sections.map((section) => (
          <Box key={section.id} id={section.id} ref={(el: HTMLDivElement | null) => (refs.current[section.id] = el)} sx={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}>
            {section.content}
          </Box>
        ))}
        {footer}
      </Content>
    </Row>
  );
};

const Row = styled(Stack)`
  flex-direction: row;
  align-items: flex-start;
  gap: ${USER_SETTING_COLUMN_GAP}px;
`;

const Nav = styled(Stack)`
  width: ${USER_SETTING_NAV_WIDTH}px;
  flex: none;
  gap: 8px;
  position: sticky;
  top: 24px;
  height: fit-content;
`;

const NavItem = styled(Box, { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>`
  border-left: 4px solid ${divider.middle};
  padding: 8px 16px 8px 12px;
  cursor: pointer;
  ${({ active }) =>
    active &&
    css`
      border-color: ${primary.main};
      pointer-events: none;
    `}
`;

const Content = styled(Stack)`
  flex: 1;
  min-width: 0;
  max-width: ${USER_SETTING_CONTENT_WIDTH}px;
  gap: ${USER_SETTING_COLUMN_GAP}px;
`;
